import * as Tabs from "@radix-ui/react-tabs";
import Tab from "components/DesignSystem/Tab/Tab";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Header } from "components/DesignSystem/Header/Header";
import { DashboardLayout } from "components/DashboardLayout";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { Button } from "components/DesignSystem/Button/Button";
import dayjs from "dayjs";
import { useFilters } from "hooks/useFilter";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { MultiValue, SingleValue } from "react-select";
import { getSeasonsFrom } from "utils/getAllSeason";
import { useGetEntityAllStateDataQuery } from "store/apis/entity";
import {
  useGetSITSalesDataQuery,
  useGetSITPayrollDataQuery,
  useGetSITPropertyDataQuery,
} from "store/apis/stateIncomeTax";
import { SITOverview } from "./SITOverview";
import { SITJurisdictions } from "./SITJurisdictions";
import { SITSalesData } from "./SITSalesData";
import { SITPayrollData } from "./SITPayrollData";
import { SITPropertyData } from "./SITPropertyData";
import { SITSettingsModal } from "./SITSettingsModal";
import classNames from "classnames";
import { Settings } from "components/icons/Navbar/Settings";

const TABS = {
  OVERVIEW: "overview",
  JURISDICTIONS: "jurisdictions",
  SALES_DATA: "sales-data",
  PAYROLL: "payroll",
  PROPERTY: "property",
} as const;

type SITTab = (typeof TABS)[keyof typeof TABS];

const TabDot = ({ done }: { done: boolean }) => (
  <span
    className={classNames("t-h-1.5 t-w-1.5 t-rounded-full t-flex-shrink-0", {
      "t-bg-green-55": done,
      "t-bg-neutral-20": !done,
    })}
  />
);

// TODO: State income tax rules are only updated for 2025 so far. Once 2026
// rules are updated, remove DEFAULT_SIT_YEAR/allow the year selector to change
// and revert to using `currentYear` as before.
const DEFAULT_SIT_YEAR = 2025;

export const StateIncomeTax = () => {
  const currentYear = dayjs().year();
  const allSeasons = getSeasonsFrom(2020, currentYear + 1);
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const settingsModal = useModal();

  const { values, updateFilter } = useFilters({
    initialValue: {
      season: String(DEFAULT_SIT_YEAR),
      tab: TABS.OVERVIEW as SITTab,
    },
    useQueryParams: true,
  });

  const season = Number(values.season) || DEFAULT_SIT_YEAR;
  const activeTab = (values.tab as SITTab) || TABS.OVERVIEW;

  const onSeasonChange = (
    option: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (option instanceof Array || !option?.value) return;
    updateFilter("season", option.value);
  };

  const seasonOptions = allSeasons.map((s) => ({
    value: String(s),
    label: String(s),
  }));

  const { data: jurisdictions = [] } = useGetEntityAllStateDataQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const { data: salesData } = useGetSITSalesDataQuery(
    { groupId, entityId, season },
    { skip: !groupId || !entityId }
  );

  const { data: payrollData } = useGetSITPayrollDataQuery(
    { groupId, entityId, season },
    { skip: !groupId || !entityId }
  );

  const { data: propertyData } = useGetSITPropertyDataQuery(
    { groupId, entityId, season },
    { skip: !groupId || !entityId }
  );

  const hasJurisdictions = jurisdictions?.length > 0;
  const hasSalesData = Boolean(salesData);
  const hasPayrollData = Boolean(payrollData);
  const hasPropertyData = Boolean(propertyData?.data?.length);

  const onTabChange = (tab: string) => updateFilter("tab", tab as SITTab);

  return (
    <>
      <DashboardLayout
        header={
          <Header
            v2
            title="State Income Tax"
            right={
              <div className="t-flex t-items-center t-gap-2">
                <EntitySelector showOnlyUSEntities size="small" />
                <Combobox
                  menuPortalTarget={document.body}
                  size="small"
                  isSearchable={false}
                  isClearable={false}
                  isDisabled
                  onChange={onSeasonChange}
                  value={
                    seasonOptions.find((o) => o.value === String(season)) ??
                    null
                  }
                  options={seasonOptions}
                />
                <Button
                  size="small"
                  customType="secondary"
                  onClick={settingsModal.open}
                >
                  <Settings />
                </Button>
              </div>
            }
            bottom={
              <Tab.Root
                value={activeTab}
                onValueChange={(v) => onTabChange(v as SITTab)}
              >
                <Tab.List>
                  <Tab.Trigger value={TABS.OVERVIEW}>Overview</Tab.Trigger>
                  <Tab.Trigger value={TABS.JURISDICTIONS}>
                    <span className="t-flex t-items-center t-gap-1.5">
                      <TabDot done={hasJurisdictions} />
                      Jurisdictions
                    </span>
                  </Tab.Trigger>
                  <Tab.Trigger value={TABS.SALES_DATA}>
                    <span className="t-flex t-items-center t-gap-1.5">
                      <TabDot done={hasSalesData} />
                      Sales Data
                    </span>
                  </Tab.Trigger>
                  <Tab.Trigger value={TABS.PAYROLL}>
                    <span className="t-flex t-items-center t-gap-1.5">
                      <TabDot done={hasPayrollData} />
                      Payroll
                    </span>
                  </Tab.Trigger>
                  <Tab.Trigger value={TABS.PROPERTY}>
                    <span className="t-flex t-items-center t-gap-1.5">
                      <TabDot done={hasPropertyData} />
                      Property
                    </span>
                  </Tab.Trigger>
                </Tab.List>
              </Tab.Root>
            }
          />
        }
      >
        <Tabs.Root value={activeTab}>
          <Tabs.Content value={TABS.OVERVIEW} className="t-pb-20">
            <SITOverview season={season} onTabChange={onTabChange} />
          </Tabs.Content>
          <Tabs.Content value={TABS.JURISDICTIONS} className="t-pb-20">
            <SITJurisdictions />
          </Tabs.Content>
          <Tabs.Content value={TABS.SALES_DATA} className="t-pb-20">
            <SITSalesData season={season} />
          </Tabs.Content>
          <Tabs.Content value={TABS.PAYROLL} className="t-pb-20">
            <SITPayrollData season={season} />
          </Tabs.Content>
          <Tabs.Content value={TABS.PROPERTY} className="t-pb-20">
            <SITPropertyData season={season} />
          </Tabs.Content>
        </Tabs.Root>
      </DashboardLayout>

      {settingsModal.isOpen && (
        <SITSettingsModal
          isOpen={settingsModal.isOpen}
          close={settingsModal.close}
          season={season}
        />
      )}
    </>
  );
};
